import * as THREE from 'three';
import { gsap } from 'gsap';

// Orthographic top-down camera locked at ~45deg pitch so voxel sides read clearly.
// Follows the player with slight lead toward the mouse cursor (classic Hotline Miami feel).

export function createCamera() {
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 200);
  return camera;
}

export function resizeOrtho(camera, viewSize = 20) {
  const aspect = window.innerWidth / window.innerHeight;
  camera.left = -viewSize * aspect * 0.5;
  camera.right = viewSize * aspect * 0.5;
  camera.top = viewSize * 0.5;
  camera.bottom = -viewSize * 0.5;
  camera.updateProjectionMatrix();
}

export class CameraRig {
  constructor(camera, { distance = 40, pitch = Math.PI / 4, viewSize = 22 } = {}) {
    this.camera = camera;
    this.distance = distance;
    this.pitch = pitch;
    this.viewSize = viewSize;
    this.target = new THREE.Vector3();
    this.follow = new THREE.Vector3();
    this.lead = new THREE.Vector3();
    this.shakeAmp = 0;
    this.shakeDecay = 7.0;
    this._distanceOffset = 0;
    this._lookOffset = new THREE.Vector3();
    this._gsapShake = { x: 0, z: 0 };
    this._tmp = new THREE.Vector3();
    resizeOrtho(camera, viewSize);
  }

  setFollow(x, z) {
    this.follow.set(x, 0, z);
  }

  setLead(x, z) {
    this.lead.set(x, 0, z);
  }

  shake(amp = 0.35) {
    this.shakeAmp = Math.min(1.2, Math.max(this.shakeAmp, amp));
  }

  punchZoom(amount = 1.3, duration = 0.22) {
    gsap.killTweensOf(this);
    const half = Math.max(0.04, duration * 0.5);
    gsap.timeline({ defaults: { overwrite: 'auto' } })
      .to(this, { _distanceOffset: -amount, duration: half, ease: 'power2.out' })
      .to(this, { _distanceOffset: 0, duration: half, ease: 'power2.inOut' });
  }

  recoilLook(x = 0, z = 0, duration = 0.16) {
    gsap.killTweensOf(this._lookOffset);
    this._lookOffset.set(x, 0, z);
    gsap.to(this._lookOffset, {
      x: 0,
      z: 0,
      duration,
      ease: 'power2.out',
      overwrite: 'auto',
    });
  }

  /**
   * Short, snappy "handy" impact shake driven by GSAP.
   * Snaps to a peak in a random (or supplied) direction, then settles with two
   * smaller, alternating bounces. Subsequent calls stack additively up to a cap
   * so rapid hits feel weighty without locking the previous tween's end state.
   */
  shakeImpactGsap({ intensity = 0.1, duration = 0.18, dirX = null, dirZ = null } = {}) {
    const target = this._gsapShake;

    let dx, dz;
    if (dirX !== null && dirZ !== null) {
      const len = Math.hypot(dirX, dirZ) || 1;
      dx = dirX / len;
      dz = dirZ / len;
    } else {
      const a = Math.random() * Math.PI * 2;
      dx = Math.cos(a);
      dz = Math.sin(a);
    }

    const cap = 0.35;
    const peak = Math.min(cap, Math.abs(target.x) + intensity);
    const jitter = () => (Math.random() * 0.4 + 0.8); // 0.8..1.2 multiplier
    const perp = () => (Math.random() * 0.6 - 0.3); // small perpendicular noise

    const px = dz, pz = -dx; // perpendicular to impact dir

    gsap.killTweensOf(target);
    gsap.timeline({ defaults: { overwrite: 'auto' } })
      // Snap to the peak almost instantly — gives the "hit" punch.
      .to(target, {
        x: dx * peak + px * peak * perp(),
        z: dz * peak + pz * peak * perp(),
        duration: duration * 0.12,
        ease: 'power4.out',
      })
      // Counter-bounce in the opposite direction, weaker.
      .to(target, {
        x: -dx * peak * 0.55 * jitter() + px * peak * perp(),
        z: -dz * peak * 0.55 * jitter() + pz * peak * perp(),
        duration: duration * 0.28,
        ease: 'power2.inOut',
      })
      // Smaller residual wobble back toward original direction.
      .to(target, {
        x: dx * peak * 0.22 * jitter(),
        z: dz * peak * 0.22 * jitter(),
        duration: duration * 0.28,
        ease: 'power2.inOut',
      })
      // Settle smoothly to zero so the camera doesn't pop.
      .to(target, {
        x: 0,
        z: 0,
        duration: duration * 0.32,
        ease: 'power3.out',
      });
  }

  resize() {
    resizeOrtho(this.camera, this.viewSize);
  }

  update(dt) {
    // Keep slight aim lead without letting spawns drift to the edge of view.
    this._tmp.copy(this.follow).lerp(this.lead, 0.14);
    this.target.lerp(this._tmp, Math.min(1, dt * 8));

    const dx = 0;
    const currentDistance = this.distance + this._distanceOffset;
    const dy = Math.sin(this.pitch) * currentDistance;
    const dz = Math.cos(this.pitch) * currentDistance;

    // Shake offset in world xz plane (small, not jarring)
    let sx = 0, sz = 0;
    if (this.shakeAmp > 0.001) {
      sx = (Math.random() * 2 - 1) * this.shakeAmp;
      sz = (Math.random() * 2 - 1) * this.shakeAmp;
      this.shakeAmp = Math.max(0, this.shakeAmp - dt * this.shakeDecay * this.shakeAmp);
    }
    sx += this._gsapShake.x;
    sz += this._gsapShake.z;

    this.camera.position.set(
      this.target.x + dx + sx,
      dy,
      this.target.z + dz + sz
    );
    this.camera.lookAt(
      this.target.x + this._lookOffset.x + sx,
      this._lookOffset.y,
      this.target.z + this._lookOffset.z + sz
    );
  }
}
